import { Box, CardContent, Grid, Typography } from "@mui/material";
import { MprcCard, MprcContainer, MprcFormSubHeader, MprcLink, MprcMessageBox, MprcRequestErrorReport, MprcRequestIndicator, MprcTypography } from "components";
import { MprcPinCodeField } from "components/MprcPinCodeField";
import { AppAuthenticatorPrepareSetupResponse } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import { QRCodeSVG } from "qrcode.react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SessionService } from "services/sessionService";

interface IProps {
    onClose: (result: "cancel" | "skipped" | "validaded") => void;
}

export const TwoFactorAppSetup = (props: IProps) => {
    const { onClose } = props;

    const api = useCoreApi();
    const { t } = useTranslation();
    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(true);
    const [viewData, setViewData] = useState<AppAuthenticatorPrepareSetupResponse>(null!);
    const [viewError, setViewError] = useState<any>(null);
    const [validationFailed, setValidationFailed] = useState(false);
    const { config: { twoFactorOptions } } = useSession();

    const load = async () => {
        try {
            var response = await api.v1.account.appAuthenticatorPrepareSetup({});
            setViewData(response.data);
        }
        catch (e) {
            setViewError(e);
        }
        finally {
            setLoading(false);
        }
    };

    const handleChange = (newValue: string) => {
        setOtp(newValue);
    };

    const handleComplete = async (pinCode: string) => {
        setLoading(true);
        setValidationFailed(false);
        try {
            var response = await api.v1.account.appAuthenticatorSetupConfirm({ pinCode });
            if (response.data === true) {
                onClose("validaded");
            }
            else {
                setValidationFailed(true);
                setOtp("");
            }
        }
        catch (e) {
            setViewError(e);
        }
        finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    return (
        <Grid container direction="column" justifyContent="center" alignItems="center" spacing={2} >
            <Grid size="grow">
                <img
                    src={SessionService.current.appImages.highlightImageUrl}
                    width="360"
                />
            </Grid>
            <Grid size="grow">

                <MprcCard sx={{ maxWidth: 450 }}>
                    <CardContent>
                        <MprcRequestIndicator type="request" visible={loading} />

                        <MprcFormSubHeader
                            title={
                                <center>{t("Auth.Login.2FA.APP.Setup.Title")}</center>
                            }
                            margin="normal"
                        />

                        {viewError
                            ? <MprcRequestErrorReport error={viewError} />
                            : viewData && viewData.deepLinkUrl != null
                                ? <>
                                    <MprcContainer>
                                        <MprcTypography variant="caption">
                                            <ul>
                                                <li>{t("Auth.Login.2FA.APP.Setup.Body1")}</li>
                                                <li>{t("Auth.Login.2FA.APP.Setup.Body2")}</li>
                                                <li>{t("Auth.Login.2FA.APP.Setup.Body3")}</li>
                                                <li>{t("Auth.Login.2FA.APP.Setup.Body4")}</li>
                                            </ul>
                                        </MprcTypography>
                                    </MprcContainer>

                                    <center>
                                        <MprcContainer margin="large">
                                            <QRCodeSVG value={viewData.deepLinkUrl} size={153} />
                                        </MprcContainer>
                                        <Typography variant="h4" color="warning">{t("Auth.Login.2FA.APP.Setup.EnterPinCodeText")}</Typography>
                                        <Box sx={{ mt: 2, mb: 4, width: "85%" }}>
                                            <MprcPinCodeField
                                                autoFocus
                                                isNumeric
                                                length={6}
                                                value={otp}
                                                onComplete={handleComplete}
                                                onChange={handleChange}
                                            />
                                        </Box>

                                        {validationFailed &&
                                            <MprcContainer>
                                                <MprcTypography variant="captionHeader" color="error">
                                                    {t("Auth.Login.2FA.APP.Setup.InvalidCodeText")}
                                                </MprcTypography>
                                            </MprcContainer>
                                        }
                                    </center>
                                    <center>
                                        {
                                            twoFactorOptions?.allowSkipAppAuthenticator === true
                                                ? <MprcLink onClick={() => onClose("skipped")}>
                                                    {t("Auth.Login.2FA.APP.Setup.SkipSetupText")}
                                                </MprcLink>
                                                : <MprcLink onClick={() => onClose("cancel")}>
                                                    {t("Auth.Login.2FA.APP.Setup.CancelText")}
                                                </MprcLink>
                                        }
                                    </center>
                                </>
                                : !loading && <MprcContainer>
                                    <MprcMessageBox
                                        message={t("Auth.Login.2FA.APP.Setup.SetupDenied")}
                                        variant="error"
                                    />
                                </MprcContainer>
                        }
                    </CardContent>
                </MprcCard>
            </Grid>
        </Grid>
    );
};